import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { formatDate } from '../../utils/helper.js';
import { statusColor, BOOKING_STATUSES } from "../../assets/utils/statusColors";
import Paginator from "../../components/Paginator";
import Swal from 'sweetalert2';
import ServicesTab from "./ServicesTab";
import { providerType } from "./providerTypes";

const CompanyDetails = ({ type = 'seller' }) => {
  const cfg = providerType(type);
  const { id } = useParams();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({});
  const [jobs, setJobs] = useState([]);
  const [jobStats, setJobStats] = useState({});
  const [isLoader, setIsLoader] = useState(false);
  const [currency] = useState(process.env.REACT_APP_CURRENCY);
  const [tab, setTab] = useState("info");
  const [company, setCompany] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [apiData, setApiData] = useState({
    search: "",
    page: 1,
    status: ''
  });

  useEffect(() => {
    if (id) {
      getSingleData();
    }
  }, [id]);

  const getSingleData = async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `companies/${id}`,
      });
      const data = response.data;
      if (data.status) {
        setCompany(data.data);
        setCurrentSubscription(data.current_subscription);
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  };

  const getJobs = async () => {
    try {
      setIsLoader(true);
      const queryParams = new URLSearchParams({
        page: apiData.page,
        ...(apiData.search && { search: apiData.search }),
        ...(apiData.status && { status: apiData.status })
      }).toString();

      const response = await ApiService.request({
        method: "GET",
        url: `companies/${id}/jobs?${queryParams}`,
      });
      const data = response.data;
      if (data.status) {
        setJobs(data.data.jobs);
        setJobStats(data.data.stats);
        setPagination(data.data.pagination);
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
      toast.error('Failed to load jobs');
    }
  };

  useEffect(() => {
    if (tab === "jobs") {
      getJobs();
    }
  }, [tab, apiData.page, apiData.search, apiData.status]);

  const handlePageChange = (event) => {
    setApiData(prev => ({ ...prev, page: event }));
  };

  const getStatusBadgeColor = (status) => statusColor[status] || 'secondary';

  const handleApprove = () => {
    Swal.fire({
      title: 'Approve Company',
      text: 'Are you sure you want to approve this company?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, approve!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoader(true);
          const response = await ApiService.request({
            method: 'POST',
            url: `companies/approval-status/${id}`,
            data: {status: 'Approved'}
          });
          if (response.data.status) {
            toast.success(response.data.message);
            getSingleData();
          } else {
            toast.error(response.data.message);
          }
        } catch (error) {
          toast.error('Error approving company');
        } finally {
          setIsLoader(false);
        }
      }
    });
  };

  const handleReject = () => {
    Swal.fire({
      title: 'Reject Company',
      html: `
        <p>Are you sure you want to reject this company?</p>
        <input id="swal-reason" class="swal2-input" placeholder="Reason for rejection">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reject!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      preConfirm: () => {
        const reason = Swal.getPopup().querySelector('#swal-reason').value;
        if (!reason) {
          Swal.showValidationMessage('Please enter a reason');
          return false;
        }
        return reason;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoader(true);
          const response = await ApiService.request({
            method: 'POST',
            url: `companies/approval-status/${id}`,
            data: {status:'Rejected', reason: result.value }
          });
          if (response.data.status) {
            toast.success(response.data.message);
            getSingleData();
          } else {
            toast.error(response.data.message);
          }
        } catch (error) {
          toast.error('Error rejecting company');
        } finally {
          setIsLoader(false);
        }
      }
    });
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {cfg.singular}: {company?.name}
              {company?.status === 'pending' && (
                <span className="badge bg-warning ms-2 fs-7">Pending Approval</span>
              )}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to={cfg.basePath} className="text-muted text-hover-primary">{cfg.title}</Link>
              </li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            {company?.approval_status === 'Pending' && (
              <>
                <button 
                  onClick={handleApprove}
                  className="btn btn-sm btn-success"
                >
                  <i className="bi bi-check-circle-fill me-2"></i> Approve
                </button>
                <button 
                  onClick={handleReject}
                  className="btn btn-sm btn-danger"
                >
                  <i className="bi bi-x-circle-fill me-2"></i> Reject
                </button>
              </>
            )}
            <Link to={cfg.basePath} className="btn btn-sm btn-light-primary">
              <i className="bi bi-arrow-left me-2"></i> Back to List
            </Link>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body pt-5">
              <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-6">
                <li className="nav-item">
                  <button 
                    className={`nav-link ${tab === "info" ? "active" : ""}`}
                    onClick={() => setTab("info")}
                  >
                    <i className="bi bi-building me-2"></i> {cfg.singular} Info
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className={`nav-link ${tab === "jobs" ? "active" : ""}`}
                    onClick={() => setTab("jobs")}
                  >
                    <i className="bi bi-calendar-check me-2"></i> Bookings
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className={`nav-link ${tab === "services" ? "active" : ""}`}
                    onClick={() => setTab("services")}
                  >
                    <i className="bi bi-tools me-2"></i> Services
                  </button>
                </li>
              </ul>

              {isLoader ? (
                <Loader color="black" className="mt-5" text="Loading" size="30" />
              ) : (
                <div className="tab-content">
                  {tab === "info" && (
                    <div className="tab-pane fade show active">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="card mb-5">
                            <div className="card-header">
                              <h3 className="card-title fw-bold">Basic Information</h3>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table table-borderless">
                                  <tbody>
                                    <tr>
                                      <th className="w-25 fw-semibold text-muted"> Name</th>
                                      <td>{company?.name}</td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Email</th>
                                      <td>{company?.email}</td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Phone</th>
                                      <td>{company?.phone}</td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">About</th>
                                      <td>{company?.about}</td>
                                    </tr>
                                  
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          <div className="card mb-5">
                            <div className="card-header">
                              <h3 className="card-title fw-bold">Current Subscription</h3>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table table-borderless">
                                  <tbody>
                                    <tr>
                                      <th className="w-25 fw-semibold text-muted">Package</th>
                                      <td>
                                        {currentSubscription?.package_id?.title ? (
                                          <span className="badge bg-primary">
                                            {currentSubscription.package_id.title}
                                          </span>
                                        ) : (
                                          <span className="text-muted">No active subscription</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Status</th>
                                      <td>
                                        {currentSubscription ? (
                                          <span className={`badge bg-${currentSubscription.status === 'Active' ? 'success' : 'warning'}`}>
                                            {currentSubscription.status}
                                          </span>
                                        ) : (
                                          <span className="badge bg-danger">Inactive</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Amount Paid</th>
                                      <td>
                                        {currentSubscription?.amount_paid ? (
                                          `${currency} ${currentSubscription.amount_paid}`
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Expiry Date</th>
                                      <td>
                                        {currentSubscription?.expiry_date ? (
                                          formatDate(currentSubscription.expiry_date, 'DD MMM YYYY, hh:mm A')
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Free Trial</th>
                                      <td>
                                        {currentSubscription?.is_free_trial ? (
                                          <span className="badge bg-info">Yes</span>
                                        ) : (
                                          <span className="badge bg-dark">No</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Subscription Date</th>
                                      <td>
                                        {currentSubscription?.createdAt ? (
                                          formatDate(currentSubscription.createdAt, 'DD MMM YYYY, hh:mm A')
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                         

                          <div className="card mb-5">
                            <div className="card-header">
                              <h3 className="card-title fw-bold">Business Details</h3>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table table-borderless">
                                  <tbody>
                                    <tr>
                                      <th className="w-25 fw-semibold text-muted">Service Type</th>
                                      <td>
                                        {company?.service_type}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Categories</th>
                                      <td>
                                        {company?.categories?.length > 0 ? (
                                          company.categories.map((category, index) => (
                                            <span key={index} className="badge bg-primary me-1 mb-1">
                                              {category.title}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Sub Categories</th>
                                      <td>
                                        {company?.sub_categories?.length > 0 ? (
                                          company.sub_categories.map((cat, index) => (
                                            <span key={index} className="badge bg-secondary text-dark me-1 mb-1">
                                              {cat?.title}
                                            </span>
                                          ))
                                        ) : 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Emirates</th>
                                      <td>
                                        {company?.emirates?.length > 0 ? (
                                          company.emirates.map((emirate, index) => (
                                            <span key={index} className="badge bg-info me-1 mb-1">
                                              {emirate.name}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>
                                    </tr>
                                    {type === 'facility' && (
                                      <tr>
                                        <th className="fw-semibold text-muted">Service Areas</th>
                                        <td>
                                          {company?.areas?.length > 0
                                            ? company.areas.map(area => <span key={area} className="badge bg-success me-1 mb-1">{area}</span>)
                                            : <span className="text-muted">N/A</span>}
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <th className="fw-semibold text-muted">Email Verified</th>
                                      <td>{company?.email_verified_at ? formatDate(company.email_verified_at, 'DD MMM YYYY, hh:mm A') : 'N/A'}</td>
                                    </tr>
                                    <tr>
                                      <th className="fw-semibold text-muted">Registered Date</th>
                                      <td>{formatDate(company?.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          <div className="card">
                            <div className="card-header">
                              <h3 className="card-title fw-bold">Documents</h3>
                            </div>
                            <div className="card-body">
                              <div className="row">
                                                                 <div className="col-md-6 mb-3">
                                   <label className="fw-semibold text-muted d-block mb-2"> License</label>
                                   {company?.license ? (
                                     <a href={company.license} target="_blank" rel="noopener noreferrer">
                                       <img 
                                         src={company.license} 
                                         alt=" License" 
                                         className="img-thumbnail w-100"
                                       />
                                     </a>
                                   ) : 'N/A'}
                                 </div>
                                 <div className="col-md-6">
                                   <label className="fw-semibold text-muted d-block mb-2">Certificate</label>
                                   {company?.certificate ? (
                                     <a href={company.certificate} target="_blank" rel="noopener noreferrer">
                                       <img 
                                         src={company.certificate} 
                                         alt=" Certificate" 
                                         className="img-thumbnail w-100"
                                       />
                                     </a>
                                   ) : 'N/A'}
                                 </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {tab === "services" && <ServicesTab companyId={id} />}

                  {tab === "jobs" && (
                    <div className="tab-pane fade show active">
                      {/* Job Statistics */}
                      <div className="row g-5 g-xl-8 mb-5">
                        <div className="col-xl-3">
                          <div className="card bg-light-primary card-xl-stretch mb-xl-8">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="text-muted fw-semibold fs-7">Total Jobs</div>
                                  <div className="fs-2 fw-bold text-primary">{jobStats.total_jobs || 0}</div>
                                </div>
                                <span className="svg-icon svg-icon-3x svg-icon-primary d-block my-2">
                                  <i className="bi bi-journals fs-1 text-primary"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-3">
                          <div className="card bg-light-warning card-xl-stretch mb-xl-8">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="text-muted fw-semibold fs-7">Requested</div>
                                  <div className="fs-2 fw-bold text-warning">{jobStats.requested_jobs || 0}</div>
                                </div>
                                <span className="svg-icon svg-icon-3x svg-icon-warning d-block my-2">
                                  <i className="bi bi-clock fs-1 text-warning"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-3">
                          <div className="card bg-light-info card-xl-stretch mb-xl-8">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="text-muted fw-semibold fs-7">Confirmed &amp; In-Progress</div>
                                  <div className="fs-2 fw-bold text-info">{jobStats.active_jobs || 0}</div>
                                </div>
                                <span className="svg-icon svg-icon-3x svg-icon-info d-block my-2">
                                  <i className="bi bi-play-circle fs-1 text-info"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-3">
                          <div className="card bg-light-success card-xl-stretch mb-xl-8">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="text-muted fw-semibold fs-7">Completed</div>
                                  <div className="fs-2 fw-bold text-success">{jobStats.completed_jobs || 0}</div>
                                </div>
                                <span className="svg-icon svg-icon-3x svg-icon-success d-block my-2">
                                  <i className="bi bi-check-circle fs-1 text-success"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Search and Filter */}
                      <div className="card mb-5">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-4">
                              <input 
                                type="text" 
                                className="form-control" 
                                placeholder="Search jobs..."
                                value={apiData.search}
                                onChange={(e) => setApiData(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                              />
                            </div>
                            <div className="col-md-3">
                              <select 
                                className="form-select"
                                value={apiData.status}
                                onChange={(e) => setApiData(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                              >
                                <option value="">All Status</option>
                                {BOOKING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                            <div className="col-md-3">
                              <button 
                                className="btn btn-secondary"
                                onClick={() => setApiData({ search: "", page: 1, status: "" })}
                              >
                                Clear Filters
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Jobs Table */}
                      <div className="card">
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-hover align-middle">
                              <thead>
                                <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                  <th>Job Code</th>
                                  <th>User</th>
                                  <th>Service</th>
                                  <th>Location</th>
                                  <th>Date & Time</th>
                                  <th>Amount</th>
                                  <th>Status</th>
                                  <th>Created Date</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody className="fw-semibold text-gray-600">
                                {jobs.length > 0 ? jobs.map((job, index) => (
                                  <tr key={index}>
                                    <td>
                                      <span className="fw-bold text-dark">{job.job_code}</span>
                                    </td>
                                    <td>
                                      {job.user_id && (
                                        <div className="d-flex align-items-center">
                                          <img 
                                            src={job.user_id.picture} 
                                            alt={job.user_id.name}
                                            className="w-40px h-40px rounded-circle me-3"
                                          />
                                          <div>
                                            <div className="fw-bold text-dark">{job.user_id.name}</div>
                                            <div className="text-muted fs-7">{job.user_id.phone}</div>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">
                                          {job.service_id?.name || 'N/A'}
                                        </div>
                                        {job.service_id?.category_id && (
                                          <div className="text-muted fs-7">
{job.service_id.category_id.title}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="text-dark">{job.location || 'N/A'}</span>
                                    </td>
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">
                                          {job.date ? formatDate(job.date, 'DD MMM YYYY') : 'N/A'}
                                        </div>
                                        {job.time && (
                                          <div className="text-muted fs-7">{job.time}</div>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="fw-bold text-dark">
                                        {job.pay_amount ? `$${job.pay_amount}` : 'N/A'}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`badge bg-${statusColor[job.status]}`}>
                                        {job.status}
                                      </span>
                                    </td>
                                    <td>{formatDate(job.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                    <td>
                                      <Link 
                                        to={`/bookings/${job._id}`} 
                                        className="btn btn-icon btn-light-primary btn-sm"
                                        title="View Job Details"
                                      >
                                        <i className="bi bi-eye-fill fs-6"></i>
                                      </Link>
                                    </td>
                                  </tr>
                                )) : (
                                  <tr>
                                    <td colSpan="9" className="text-center py-10">
                                      <div className="text-muted">No jobs found for this company</div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {pagination.total_pages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-5">
                              <div className="text-muted">
                                Showing {jobs.length} of {pagination.total_records} records
                              </div>
                              <Paginator
                                page={pagination.current_page}
                                rows={pagination.per_page}
                                totalRecords={pagination.total_records}
                                onPageChange={handlePageChange}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;